import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import JsonEditor from './JsonEditor';
import { PromptProfile } from '../api/promptManagement';
import JsonPromptProfileEditor from './JsonPromptProfileEditor';

// Type declarations for styled-components props
interface DialogOverlayProps {
  $isOpen: boolean;
}

interface ButtonProps {
  $primary?: boolean;
  $danger?: boolean;
  $disabled?: boolean;
}

interface TabProps {
  $active: boolean;
}

interface TabContentProps {
  $active: boolean;
}

interface ToggleButtonProps {
  $active: boolean;
}

interface StatusMessageProps {
  $type: 'success' | 'error' | 'info';
}

// Styled Components
const DialogOverlay = styled.div<DialogOverlayProps>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: ${(props: DialogOverlayProps) => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const DialogContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 95vw;
  max-width: 1200px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const DialogHeader = styled.div`
  padding: 24px 32px 20px;
  border-bottom: 1px solid #e1e5e9;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
`;

const DialogTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 600;
`;

const DialogSubtitle = styled.p`
  margin: 8px 0 0 0;
  opacity: 0.9;
  font-size: 14px;
`;

const DialogBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 32px;
`;

const DialogFooter = styled.div`
  padding: 20px 32px;
  border-top: 1px solid #e1e5e9;
  background: #f8f9fa;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const FormSection = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h3`
  color: #2c3e50;
  margin: 0 0 16px 0;
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionIcon = styled.span`
  font-size: 20px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
`;

const RequiredLabel = styled(Label)`
  &::after {
    content: ' *';
    color: #e74c3c;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #007bff;
  }

  &:disabled {
    background: #f8f9fa;
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  min-height: 100px;
  resize: vertical;
  transition: border-color 0.2s;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #007bff;
  }

  &:disabled {
    background: #f8f9fa;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  background: white;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #007bff;
  }

  &:disabled {
    background: #f8f9fa;
    cursor: not-allowed;
  }
`;

const Button = styled.button<ButtonProps>`
  padding: 12px 24px;
  border: 2px solid ${(props: ButtonProps) => {
    if (props.$disabled) return '#bdc3c7';
    if (props.$danger) return '#e74c3c';
    if (props.$primary) return '#667eea';
    return '#e1e5e9';
  }};
  border-radius: 8px;
  background: ${(props: ButtonProps) => {
    if (props.$disabled) return '#bdc3c7';
    if (props.$danger) return '#e74c3c';
    if (props.$primary) return '#667eea';
    return 'white';
  }};
  color: ${(props: ButtonProps) => props.$disabled ? '#7f8c8d' : props.$primary || props.$danger ? 'white' : '#2c3e50'};
  font-size: 14px;
  font-weight: 600;
  cursor: ${(props: ButtonProps) => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;

  &:hover {
    background: ${(props: ButtonProps) => {
      if (props.$disabled) return '#bdc3c7';
      if (props.$danger) return '#c0392b';
      if (props.$primary) return '#5a6fd8';
      return '#f8f9fa';
    }};
    transform: ${(props: ButtonProps) => props.$disabled ? 'none' : 'translateY(-1px)'};
  }

  &:active {
    transform: ${(props: ButtonProps) => props.$disabled ? 'none' : 'translateY(0)'};
  }
`;

const HelpText = styled.div`
  color: #6c757d;
  font-size: 12px;
  margin-top: 4px;
  line-height: 1.4;
`;

const ErrorText = styled.div`
  color: #e74c3c;
  font-size: 12px;
  margin-top: 4px;
`;

const SuccessText = styled.div`
  color: #27ae60;
  font-size: 12px;
  margin-top: 4px;
`;

const TabsContainer = styled.div`
  border-bottom: 2px solid #e1e5e9;
  margin-bottom: 24px;
`;

const TabsList = styled.div`
  display: flex;
  gap: 0;
`;

const Tab = styled.button<TabProps>`
  padding: 12px 24px;
  border: none;
  background: ${(props: TabProps) => props.$active ? '#667eea' : 'transparent'};
  color: ${(props: TabProps) => props.$active ? 'white' : '#6c757d'};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 8px 8px 0 0;
  margin-right: 4px;

  &:hover {
    background: ${(props: TabProps) => props.$active ? '#667eea' : '#f8f9fa'};
    color: ${(props: TabProps) => props.$active ? 'white' : '#2c3e50'};
  }
`;

const TabContent = styled.div<TabContentProps>`
  display: ${(props: TabContentProps) => props.$active ? 'block' : 'none'};
`;

const FocusAreasContainer = styled.div`
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  padding: 16px;
  background: #f8f9fa;
`;

const AssetTypeSection = styled.div`
  margin-bottom: 20px;
  padding: 16px;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  background: white;
`;

const AssetTypeTitle = styled.h4`
  margin: 0 0 12px 0;
  color: #2c3e50;
  font-size: 16px;
  font-weight: 600;
`;

const FocusAreaInput = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.875rem;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const AddButton = styled.button`
  padding: 6px 12px;
  border: 1px solid #667eea;
  background: #667eea;
  color: white;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #5a6fd8;
  }
`;

const RemoveButton = styled.button`
  padding: 4px 8px;
  border: 1px solid #e74c3c;
  background: #e74c3c;
  color: white;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  margin-left: 8px;

  &:hover {
    background: #c0392b;
  }
`;

const FocusAreaItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`;

const ModeToggle = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 20px;
`;

const ToggleButton = styled.button<ToggleButtonProps>`
  padding: 8px 16px;
  border: 2px solid ${(props: ToggleButtonProps) => props.$active ? '#667eea' : '#e1e5e9'};
  border-radius: 6px;
  background: ${(props: ToggleButtonProps) => props.$active ? '#667eea' : 'white'};
  color: ${(props: ToggleButtonProps) => props.$active ? 'white' : '#2c3e50'};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${(props: ToggleButtonProps) => props.$active ? '#5a6fd8' : '#f8f9fa'};
  }
`;

const EditorContainer = styled.div`
  margin-top: 20px;
`;

const VersionContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const VersionInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 2px solid #e1e5e9;
  border-radius: 6px;
  font-size: 14px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  background: white;

  &:focus {
    outline: none;
    border-color: #667eea;
  }

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
  }
`;

const VersionControls = styled.div`
  display: flex;
  gap: 4px;
`;

const VersionButton = styled.button`
  padding: 6px 10px;
  border: 1px solid #667eea;
  border-radius: 4px;
  background: #667eea;
  color: white;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  min-width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: #5a67d8;
    border-color: #5a67d8;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ErrorMessage = styled.div`
  color: #e53e3e;
  font-size: 12px;
  margin-top: 4px;
  font-weight: 500;
`;

// Types
export interface EnhancedPromptProfileDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (profile: PromptProfile) => Promise<void>;
  profile?: PromptProfile;
  mode: 'create' | 'edit';
}

const DEFAULT_ASSET_TYPES = ['image', 'video', 'animated', '3d_model', '3d_animation', 'audio'];

const DEFAULT_FOCUS_AREAS = {
  image: [
    'Visual composition and balance',
    'Color harmony and palette',
    'Lighting and shadows',
    'Subject clarity and focus',
    'Artistic style and technique'
  ],
  video: [
    'Motion and animation quality',
    'Frame rate and smoothness',
    'Scene transitions',
    'Visual effects and compositing',
    'Audio-visual synchronization'
  ],
  animated: [
    'Animation smoothness and fluidity',
    'Character movement and physics',
    'Loop quality and seamlessness',
    'Visual style consistency',
    'Performance optimization'
  ],
  '3d_model': [
    'Model geometry and topology',
    'Texture quality and mapping',
    'Material and shader quality',
    'Polygon count optimization',
    'UV unwrapping quality'
  ],
  '3d_animation': [
    'Character rigging and bone structure',
    'Animation keyframe quality',
    'Physics simulation accuracy',
    'Rendering quality and lighting',
    'Performance and optimization'
  ],
  audio: [
    'Audio quality and clarity',
    'Frequency balance and EQ',
    'Dynamic range and compression',
    'Spatial positioning and stereo',
    'Background noise reduction'
  ]
};

const DEFAULT_LLM_PARAMETERS = {
  temperature: 0.7,
  max_tokens: 1000,
  top_p: 1.0,
  frequency_penalty: 0.0,
  presence_penalty: 0.0
};

const DEFAULT_METADATA_SCHEMA = {
  quality_score: { type: "number", required: true, min: 0, max: 10 },
  tags: { type: "array", required: true },
  description: { type: "string", required: true, min_length: 50 },
  category: { type: "string", required: false },
  technical_notes: { type: "string", required: false }
};

const DEFAULT_VALIDATION_RULES = {
  required_fields: ["quality_score", "tags", "description"],
  min_description_length: 50,
  max_tags: 10,
  quality_score_range: { min: 0, max: 10 }
};

export const EnhancedPromptProfileDialog: React.FC<EnhancedPromptProfileDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  profile,
  mode
}) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [editMode, setEditMode] = useState<'fields' | 'json'>('json');
  const [formData, setFormData] = useState<PromptProfile>({
    profile_id: '', // Add missing required field
    name: '',
    prompt_type: 'image',
    asset_type: 'mixed',
    description: '',
    version: '1.0.0', // Will be dynamically generated by backend
    system_role_definition: '',
    custom_system_instructions: '',
    custom_user_instructions: '',
    custom_system_focus_areas: {},
    custom_user_focus_areas: {},
    custom_focus_areas: {},
    custom_metadata_schema: {},
    custom_output_format: '',
    validation_rules: {},
    llm_parameters: {
      temperature: 0.7,
      max_tokens: 1000,
      top_p: 1.0,
      frequency_penalty: 0.0,
      presence_penalty: 0.0
    },
    allowed_project_ids: []
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Version management functions
  const handleVersionIncrement = (type: 'major' | 'minor' | 'patch') => {
    const currentVersion = formData.version || '1.0.0';
    const versionParts = currentVersion.split('.').map(Number);
    
    if (versionParts.length !== 3 || versionParts.some(isNaN)) {
      setErrors({ ...errors, version: 'Invalid version format. Use MAJOR.MINOR.PATCH' });
      return;
    }

    let [major, minor, patch] = versionParts;
    
    switch (type) {
      case 'major':
        major += 1;
        minor = 0;
        patch = 0;
        break;
      case 'minor':
        minor += 1;
        patch = 0;
        break;
      case 'patch':
        patch += 1;
        break;
    }

    const newVersion = `${major}.${minor}.${patch}`;
    setFormData({ ...formData, version: newVersion });
    
    // Clear any version errors
    if (errors.version) {
      setErrors({ ...errors, version: '' });
    }
  };

  // Debug: Log formData changes
  useEffect(() => {
    console.log('🔍 FormData changed:', formData);
  }, [formData]);

  // Initialize form data when profile changes
  useEffect(() => {
    console.log('🔍 EnhancedPromptProfileDialog useEffect triggered:', { profile, mode, isOpen });
    
    if (profile && mode === 'edit') {
      console.log('🔍 Loading profile data into form:', profile);
      console.log('🔍 Profile field values:', {
        name: profile.name,
        asset_type: profile.asset_type,
        prompt_type: profile.prompt_type,
        description: profile.description,
        custom_system_instructions: profile.custom_system_instructions,
        custom_user_instructions: profile.custom_user_instructions,
        system_role_definition: profile.system_role_definition
      });
      
      // Add default instructions if the profile has empty instruction fields
      const getDefaultInstructions = (promptType: string, assetType: string) => {
        const defaults = {
          system_role_definition: `You are an expert ${promptType} analysis AI with deep expertise in visual content analysis, metadata extraction, and quality assessment. You specialize in providing comprehensive, accurate, and detailed analysis of ${promptType} content with a focus on technical quality, artistic merit, and practical applications.`,
          custom_system_instructions: `Analyze the provided ${promptType} content thoroughly and systematically. Focus on technical quality, visual composition, and content relevance. Provide detailed insights that are both technically accurate and practically useful for content creators and analysts.`,
          custom_user_instructions: `Please analyze this ${promptType} content and provide a comprehensive assessment including quality score, detailed description, relevant tags, and technical notes. Focus on the most important aspects that would be valuable for content management and optimization.`,
          // Add default focus areas
          custom_system_focus_areas: {
            [assetType]: [
              "Technical quality assessment",
              "Visual composition analysis", 
              "Content relevance evaluation",
              "Metadata extraction"
            ]
          },
          custom_user_focus_areas: {
            [assetType]: [
              "Quality scoring",
              "Detailed description generation",
              "Tag and category identification",
              "Technical recommendations"
            ]
          },
          // Add default metadata schema
          custom_metadata_schema: {
            "quality_score": {
              "type": "number",
              "description": "Overall quality score from 0-10",
              "min": 0,
              "max": 10
            },
            "description": {
              "type": "string", 
              "description": "Detailed description of the content"
            },
            "tags": {
              "type": "array",
              "items": {"type": "string"},
              "description": "Relevant tags for the content"
            },
            "category": {
              "type": "string",
              "description": "Content category classification"
            },
            "technical_notes": {
              "type": "string",
              "description": "Technical observations and recommendations"
            }
          }
        };
        return defaults;
      };

      const defaultInstructions = getDefaultInstructions(profile.prompt_type, profile.asset_type);
      
      const newFormData = {
        ...profile,
        // Use profile instructions if they exist, otherwise use defaults
        system_role_definition: profile.system_role_definition || defaultInstructions.system_role_definition,
        custom_system_instructions: profile.custom_system_instructions || defaultInstructions.custom_system_instructions,
        custom_user_instructions: profile.custom_user_instructions || defaultInstructions.custom_user_instructions,
        // Use profile focus areas if they exist, otherwise use defaults
        custom_system_focus_areas: Object.keys(profile.custom_system_focus_areas || {}).length > 0 
          ? profile.custom_system_focus_areas 
          : defaultInstructions.custom_system_focus_areas,
        custom_user_focus_areas: Object.keys(profile.custom_user_focus_areas || {}).length > 0 
          ? profile.custom_user_focus_areas 
          : defaultInstructions.custom_user_focus_areas,
        custom_focus_areas: profile.custom_focus_areas || {},
        // Use profile metadata schema if it exists, otherwise use defaults
        custom_metadata_schema: Object.keys(profile.custom_metadata_schema || {}).length > 0 
          ? profile.custom_metadata_schema 
          : defaultInstructions.custom_metadata_schema,
        validation_rules: profile.validation_rules || DEFAULT_VALIDATION_RULES,
        llm_parameters: profile.llm_parameters || DEFAULT_LLM_PARAMETERS,
        allowed_project_ids: profile.allowed_project_ids || []
      };
      console.log('🔍 New form data to be set:', newFormData);
      setFormData(newFormData);
      console.log('🔍 Form data set for edit mode');
    } else {
      console.log('🔍 Resetting form data for create mode');
      // Reset to defaults for create mode
      setFormData({
        profile_id: '', // Add missing required field
        name: '',
        prompt_type: 'image',
        asset_type: 'mixed',
        description: '',
        version: '1.0.0', // Will be dynamically generated by backend
        system_role_definition: '',
        custom_system_instructions: '',
        custom_user_instructions: '',
        custom_system_focus_areas: {},
        custom_user_focus_areas: {},
        custom_focus_areas: {},
        custom_metadata_schema: DEFAULT_METADATA_SCHEMA,
        validation_rules: DEFAULT_VALIDATION_RULES,
        llm_parameters: DEFAULT_LLM_PARAMETERS,
        allowed_project_ids: []
      });
    }
    setErrors({});
    setSuccessMessage('');
  }, [profile, mode, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Profile name is required';
    }

    if (!formData.asset_type.trim()) {
      newErrors.asset_type = 'Asset type is required';
    }

    if (!formData.custom_system_instructions?.trim() && !formData.custom_user_instructions?.trim()) {
      newErrors.instructions = 'At least one instruction field is required';
    }

    // Validate version format (only for edit mode)
    if (mode === 'edit' && formData.version) {
      const versionRegex = /^\d+\.\d+\.\d+$/;
      if (!versionRegex.test(formData.version)) {
        newErrors.version = 'Version must be in format MAJOR.MINOR.PATCH (e.g., 1.0.0)';
      } else {
        const versionParts = formData.version.split('.').map(Number);
        if (versionParts.some(isNaN) || versionParts.some(v => v < 0)) {
          newErrors.version = 'Version numbers must be non-negative integers';
        }
      }
    }

    // Validate LLM parameters
    if (formData.llm_parameters) {
      if (formData.llm_parameters.temperature !== undefined && 
          (formData.llm_parameters.temperature < 0 || formData.llm_parameters.temperature > 2)) {
        newErrors.llm_parameters = 'Temperature must be between 0 and 2';
      }
      if (formData.llm_parameters.max_tokens !== undefined && formData.llm_parameters.max_tokens <= 0) {
        newErrors.llm_parameters = 'Max tokens must be greater than 0';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (editMode === 'json') {
      // In JSON mode, trigger the JSON editor's save function
      if (jsonEditorRef.current && jsonEditorRef.current.handleSave) {
        await jsonEditorRef.current.handleSave();
      }
      return;
    }

    // In field mode, use the existing logic
    if (!validateForm()) {
      return;
    }

    try {
      setIsSaving(true);
      setErrors({});
      
      // For auto-update: only include version if user explicitly changed it
      // If version is unchanged from original, don't send it (triggers auto-update)
      const profileData: any = { ...formData };
      
      if (mode === 'edit' && profile) {
        // Check if version was manually changed
        const originalVersion = profile.version || "1.0.0";
        const currentVersion = formData.version || "1.0.0";
        
        if (currentVersion === originalVersion) {
          // Version unchanged - remove it to trigger auto-update
          delete profileData.version;
          console.log('🔍 Field Editor - Auto-update mode (version unchanged)');
        } else {
          // Version changed - include it for manual control
          console.log('🔍 Field Editor - Manual version control:', currentVersion);
        }
      } else {
        // Create mode - don't include version (backend will generate)
        delete profileData.version;
        console.log('🔍 Field Editor - Create mode (no version)');
      }
      
      await onSave(profileData);
      
      setSuccessMessage('Profile saved successfully!');
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      setErrors({ general: error instanceof Error ? error.message : 'Failed to save profile' });
    } finally {
      setIsSaving(false);
    }
  };

  const jsonEditorRef = useRef<any>(null);

  const handleJsonSave = async (jsonProfile: PromptProfile) => {
    try {
      await onSave(jsonProfile);
      setSuccessMessage('Profile saved successfully!');
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error saving JSON profile:', error);
      setErrors({ general: error instanceof Error ? error.message : 'Failed to save profile' });
    }
  };

  const handleFocusAreaChange = (
    type: 'system' | 'user',
    assetType: string,
    index: number,
    value: string
  ) => {
    const field = type === 'system' ? 'custom_system_focus_areas' : 'custom_user_focus_areas';
    const currentAreas = formData[field] || {};
    const areas = [...(currentAreas[assetType] || [])];
    areas[index] = value;
    
    setFormData({
      ...formData,
      [field]: {
        ...currentAreas,
        [assetType]: areas
      }
    });
  };

  const addFocusArea = (type: 'system' | 'user', assetType: string) => {
    const field = type === 'system' ? 'custom_system_focus_areas' : 'custom_user_focus_areas';
    const currentAreas = formData[field] || {};
    const areas = [...(currentAreas[assetType] || []), ''];
    
    setFormData({
      ...formData,
      [field]: {
        ...currentAreas,
        [assetType]: areas
      }
    });
  };

  const removeFocusArea = (type: 'system' | 'user', assetType: string, index: number) => {
    const field = type === 'system' ? 'custom_system_focus_areas' : 'custom_user_focus_areas';
    const currentAreas = formData[field] || {};
    const areas = [...(currentAreas[assetType] || [])];
    areas.splice(index, 1);
    
    setFormData({
      ...formData,
      [field]: {
        ...currentAreas,
        [assetType]: areas
      }
    });
  };

  const loadDefaultFocusAreas = (type: 'system' | 'user') => {
    const field = type === 'system' ? 'custom_system_focus_areas' : 'custom_user_focus_areas';
    setFormData({
      ...formData,
      [field]: DEFAULT_FOCUS_AREAS
    });
  };

  // NEW: Load default instruction functions
  const loadDefaultSystemRoleDefinition = () => {
    const defaultRole = `You are an expert ${formData.prompt_type} analysis AI with deep expertise in visual content analysis, metadata extraction, and quality assessment. You specialize in providing comprehensive, accurate, and detailed analysis of ${formData.prompt_type} content with a focus on technical quality, artistic merit, and practical applications.`;
    setFormData({
      ...formData,
      system_role_definition: defaultRole
    });
  };

  const loadDefaultSystemInstructions = () => {
    const defaultInstructions = `Analyze the provided ${formData.prompt_type} content thoroughly and systematically. Focus on technical quality, visual composition, and content relevance. Provide detailed insights that are both technically accurate and practically useful for content creators and analysts.`;
    setFormData({
      ...formData,
      custom_system_instructions: defaultInstructions
    });
  };

  const loadDefaultUserInstructions = () => {
    const defaultInstructions = `Please analyze this ${formData.prompt_type} content and provide a comprehensive assessment including quality score, detailed description, relevant tags, and technical notes. Focus on the most important aspects that would be valuable for content management and optimization.`;
    setFormData({
      ...formData,
      custom_user_instructions: defaultInstructions
    });
  };

  const handleProjectIdsChange = (value: string) => {
    try {
      const projectIds = value.trim() ? JSON.parse(value) : [];
      if (Array.isArray(projectIds)) {
        setFormData({
          ...formData,
          allowed_project_ids: projectIds
        });
      }
    } catch (error) {
      // Invalid JSON - keep as string for now
    }
  };

  const tabs = [
    { key: 'basic', label: 'Basic Info', icon: '📋' },
    { key: 'instructions', label: 'Instructions', icon: '✍️' },
    { key: 'focus', label: 'Focus Areas', icon: '🎯' },
    { key: 'configuration', label: 'Configuration', icon: '⚙️' },
    { key: 'access', label: 'Access Control', icon: '🔒' }
  ];

  return (
    <DialogOverlay $isOpen={isOpen} onClick={onClose}>
      <DialogContainer onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Create New Prompt Profile' : 'Edit Prompt Profile'}
          </DialogTitle>
          <DialogSubtitle>
            Configure comprehensive prompt settings with system/user focus areas and role-based instructions
          </DialogSubtitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </DialogHeader>

        <DialogBody>
          <ModeToggle>
            <span style={{ fontSize: '14px', color: '#6c757d' }}>Edit Mode:</span>
            <ToggleButton 
              $active={editMode === 'fields'} 
              onClick={() => setEditMode('fields')}
            >
              Field Editor
            </ToggleButton>
            <ToggleButton 
              $active={editMode === 'json'} 
              onClick={() => setEditMode('json')}
            >
              JSON Editor
            </ToggleButton>
          </ModeToggle>

          {editMode === 'fields' ? (
            <EditorContainer>
              <TabsContainer>
                <TabsList>
                  {tabs.map(tab => (
                    <Tab
                      key={tab.key}
                      $active={activeTab === tab.key}
                      onClick={() => setActiveTab(tab.key)}
                    >
                      {tab.icon} {tab.label}
                    </Tab>
                  ))}
                </TabsList>
              </TabsContainer>

              {errors.general && (
                <ErrorText style={{ marginBottom: '16px', padding: '12px', background: '#fdf2f2', border: '1px solid #fecaca', borderRadius: '8px' }}>
                  {errors.general}
                </ErrorText>
              )}

              {successMessage && (
                <SuccessText style={{ marginBottom: '16px', padding: '12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px' }}>
                  {successMessage}
                </SuccessText>
              )}

              {/* Basic Information Tab */}
              <TabContent $active={activeTab === 'basic'}>
                <FormSection>
                  <SectionTitle>
                    <SectionIcon>📋</SectionIcon>
                    Basic Information
                  </SectionTitle>
                  
                  {/* Debug info */}
                  {process.env.NODE_ENV === 'development' && (
                    <div style={{ background: '#f0f0f0', padding: '10px', marginBottom: '20px', fontSize: '12px' }}>
                      <strong>Debug - Form Data:</strong><br/>
                      Name: {formData.name}<br/>
                      Asset Type: {formData.asset_type}<br/>
                      Prompt Type: {formData.prompt_type}<br/>
                      Description: {formData.description}<br/>
                      System Instructions: {formData.custom_system_instructions?.substring(0, 50)}...<br/>
                      User Instructions: {formData.custom_user_instructions?.substring(0, 50)}...
                    </div>
                  )}
                  
                  <FormGrid>
                    <FormGroup>
                      <RequiredLabel>Profile Name</RequiredLabel>
                      <Input
                        type="text"
                        value={formData.name}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Enter profile name"
                      />
                      {errors.name && <ErrorText>{errors.name}</ErrorText>}
                    </FormGroup>

                    <FormGroup>
                      <RequiredLabel>Asset Type</RequiredLabel>
                      <Input
                        type="text"
                        value={formData.asset_type}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, asset_type: e.target.value })}
                        placeholder="e.g., mixed, product, landscape"
                      />
                      {errors.asset_type && <ErrorText>{errors.asset_type}</ErrorText>}
                    </FormGroup>
                  </FormGrid>

                  <FormGroup>
                    <Label>Description</Label>
                    <TextArea
                      value={formData.description || ''}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Describe the purpose and scope of this profile"
                    />
                  </FormGroup>

                  <FormGrid>
                    <FormGroup>
                      <RequiredLabel>Prompt Type</RequiredLabel>
                      <Select
                        value={formData.prompt_type}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFormData({ ...formData, prompt_type: e.target.value as 'image' | 'video' })}
                      >
                        <option value="image">Image Analysis</option>
                        <option value="video">Video Analysis</option>
                      </Select>
                    </FormGroup>

                    <FormGroup>
                      <Label>Version Management</Label>
                      <VersionContainer>
                        <VersionInput
                          type="text"
                          value={formData.version || '1.0.0'}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, version: e.target.value })}
                          placeholder="1.0.0"
                          disabled={mode === 'create'} // Auto-generated for new profiles
                        />
                        {mode === 'edit' && (
                          <VersionControls>
                            <VersionButton 
                              onClick={() => handleVersionIncrement('patch')}
                              title="Increment patch version (bug fixes)"
                            >
                              +P
                            </VersionButton>
                            <VersionButton 
                              onClick={() => handleVersionIncrement('minor')}
                              title="Increment minor version (new features)"
                            >
                              +M
                            </VersionButton>
                            <VersionButton 
                              onClick={() => handleVersionIncrement('major')}
                              title="Increment major version (breaking changes)"
                            >
                              +J
                            </VersionButton>
                          </VersionControls>
                        )}
                      </VersionContainer>
                      <HelpText>
                        {mode === 'create' 
                          ? 'Version will be automatically generated for new profiles'
                          : 'Use +P (patch), +M (minor), +J (major) buttons or manually edit version'
                        }
                      </HelpText>
                      {errors.version && (
                        <ErrorMessage>{errors.version}</ErrorMessage>
                      )}
                    </FormGroup>
                  </FormGrid>
                </FormSection>
              </TabContent>

              {/* Instructions Tab */}
              <TabContent $active={activeTab === 'instructions'}>
                <FormSection>
                  <SectionTitle>
                    <SectionIcon>✍️</SectionIcon>
                    System and User Instructions
                  </SectionTitle>
                  
                  <div style={{ marginBottom: '20px' }}>
                    <Button onClick={loadDefaultSystemRoleDefinition} style={{ marginRight: '10px' }}>
                      Load Default Role Definition
                    </Button>
                    <Button onClick={loadDefaultSystemInstructions} style={{ marginRight: '10px' }}>
                      Load Default System Instructions
                    </Button>
                    <Button onClick={loadDefaultUserInstructions}>
                      Load Default User Instructions
                    </Button>
                  </div>
                  
                  <FormGroup>
                    <Label>System Role Definition</Label>
                    <TextArea
                      value={formData.system_role_definition || ''}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, system_role_definition: e.target.value })}
                      placeholder="Define the AI's role and expertise for system-level instructions..."
                    />
                    <HelpText>
                      Define the AI's role, expertise, and system-level behavior. This sets the context for how the AI should approach the analysis.
                    </HelpText>
                  </FormGroup>

                  <FormGroup>
                    <Label>System Instructions</Label>
                    <TextArea
                      value={formData.custom_system_instructions || ''}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, custom_system_instructions: e.target.value })}
                      placeholder="Define the system-level instructions and analysis approach..."
                    />
                    <HelpText>
                      Define the system-level instructions that guide how the AI should approach the analysis task.
                    </HelpText>
                  </FormGroup>

                  <FormGroup>
                    <Label>User Instructions (Analysis Requirements)</Label>
                    <TextArea
                      value={formData.custom_user_instructions || ''}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, custom_user_instructions: e.target.value })}
                      placeholder="Specify the analysis requirements and user-level instructions..."
                    />
                    <HelpText>
                      Define what the AI should analyze and how it should structure the response. This includes specific requirements and focus areas.
                    </HelpText>
                  </FormGroup>

                  {errors.instructions && <ErrorText>{errors.instructions}</ErrorText>}
                </FormSection>
              </TabContent>

              {/* Focus Areas Tab */}
              <TabContent $active={activeTab === 'focus'}>
                <FormSection>
                  <SectionTitle>
                    <SectionIcon>🎯</SectionIcon>
                    Focus Areas Configuration
                  </SectionTitle>
                  
                  <HelpText style={{ marginBottom: '20px' }}>
                    Configure focus areas for different asset types. These will be applied to both system and user prompts.
                  </HelpText>

                  <div style={{ marginBottom: '20px' }}>
                    <Button onClick={() => loadDefaultFocusAreas('system')} style={{ marginRight: '10px' }}>
                      Load Default System Focus Areas
                    </Button>
                    <Button onClick={() => loadDefaultFocusAreas('user')}>
                      Load Default User Focus Areas
                    </Button>
                  </div>

                  {DEFAULT_ASSET_TYPES.map(assetType => (
                    <AssetTypeSection key={assetType}>
                      <AssetTypeTitle>{assetType.charAt(0).toUpperCase() + assetType.slice(1)} Assets</AssetTypeTitle>
                      
                      {/* System Focus Areas */}
                      <div style={{ marginBottom: '16px' }}>
                        <Label>System Focus Areas</Label>
                        {(formData.custom_system_focus_areas?.[assetType] || []).map((area: string, index: number) => (
                          <FocusAreaItem key={`system-${assetType}-${index}`}>
                            <FocusAreaInput
                              value={area}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFocusAreaChange('system', assetType, index, e.target.value)}
                              placeholder={`System focus area ${index + 1}`}
                            />
                            <RemoveButton onClick={() => removeFocusArea('system', assetType, index)}>
                              Remove
                            </RemoveButton>
                          </FocusAreaItem>
                        ))}
                        <AddButton onClick={() => addFocusArea('system', assetType)}>
                          Add System Focus Area
                        </AddButton>
                      </div>

                      {/* User Focus Areas */}
                      <div>
                        <Label>User Focus Areas</Label>
                        {(formData.custom_user_focus_areas?.[assetType] || []).map((area: string, index: number) => (
                          <FocusAreaItem key={`user-${assetType}-${index}`}>
                            <FocusAreaInput
                              value={area}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFocusAreaChange('user', assetType, index, e.target.value)}
                              placeholder={`User focus area ${index + 1}`}
                            />
                            <RemoveButton onClick={() => removeFocusArea('user', assetType, index)}>
                              Remove
                            </RemoveButton>
                          </FocusAreaItem>
                        ))}
                        <AddButton onClick={() => addFocusArea('user', assetType)}>
                          Add User Focus Area
                        </AddButton>
                      </div>
                    </AssetTypeSection>
                  ))}
                </FormSection>
              </TabContent>

              {/* Configuration Tab */}
              <TabContent $active={activeTab === 'configuration'}>
                <FormSection>
                  <SectionTitle>
                    <SectionIcon>⚙️</SectionIcon>
                    Advanced Configuration
                  </SectionTitle>
                  
                  <FormGroup>
                    <Label>Metadata Schema (JSON)</Label>
                    <JsonEditor
                      value={JSON.stringify(formData.custom_metadata_schema || {}, null, 2)}
                      onChange={(value: string) => {
                        try {
                          const schema = value.trim() ? JSON.parse(value) : {};
                          setFormData({ ...formData, custom_metadata_schema: schema });
                        } catch (error) {
                          // Invalid JSON - keep as string for now
                        }
                      }}
                      placeholder={JSON.stringify(DEFAULT_METADATA_SCHEMA, null, 2)}
                    />
                    <HelpText>
                      Define the structure and validation rules for metadata fields in the analysis output.
                    </HelpText>
                  </FormGroup>

                  <FormGroup>
                    <Label>LLM Parameters (JSON)</Label>
                    <JsonEditor
                      value={JSON.stringify(formData.llm_parameters || {}, null, 2)}
                      onChange={(value: string) => {
                        try {
                          const params = value.trim() ? JSON.parse(value) : {};
                          setFormData({ ...formData, llm_parameters: params });
                        } catch (error) {
                          // Invalid JSON - keep as string for now
                        }
                      }}
                      placeholder={JSON.stringify(DEFAULT_LLM_PARAMETERS, null, 2)}
                    />
                    <HelpText>
                      Configure LLM parameters like temperature, max tokens, and other generation settings.
                    </HelpText>
                    {errors.llm_parameters && <ErrorText>{errors.llm_parameters}</ErrorText>}
                  </FormGroup>

                  <FormGroup>
                    <Label>Validation Rules (JSON)</Label>
                    <JsonEditor
                      value={JSON.stringify(formData.validation_rules || {}, null, 2)}
                      onChange={(value: string) => {
                        try {
                          const rules = value.trim() ? JSON.parse(value) : {};
                          setFormData({ ...formData, validation_rules: rules });
                        } catch (error) {
                          // Invalid JSON - keep as string for now
                        }
                      }}
                      placeholder={JSON.stringify(DEFAULT_VALIDATION_RULES, null, 2)}
                    />
                    <HelpText>
                      Define validation rules for the analysis output, including required fields and constraints.
                    </HelpText>
                  </FormGroup>
                </FormSection>
              </TabContent>

              {/* Access Control Tab */}
              <TabContent $active={activeTab === 'access'}>
                <FormSection>
                  <SectionTitle>
                    <SectionIcon>🔒</SectionIcon>
                    Access Control
                  </SectionTitle>
                  
                  <FormGroup>
                    <Label>Allowed Project IDs (JSON Array)</Label>
                    <JsonEditor
                      value={JSON.stringify(formData.allowed_project_ids || [], null, 2)}
                      onChange={handleProjectIdsChange}
                      placeholder='["project-123", "project-456"]'
                    />
                    <HelpText>
                      List of project IDs that can use this custom prompt. Leave empty for global access.
                    </HelpText>
                  </FormGroup>

                  <FormGroup>
                    <Label>Is Default Profile</Label>
                    <Select
                      value={formData.is_default ? 'true' : 'false'}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFormData({ ...formData, is_default: e.target.value === 'true' })}
                    >
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </Select>
                    <HelpText>
                      Mark this profile as the default for its prompt type and asset type combination.
                    </HelpText>
                  </FormGroup>
                </FormSection>
              </TabContent>
            </EditorContainer>
          ) : (
            <EditorContainer>
              <JsonPromptProfileEditor
                ref={jsonEditorRef}
                profile={profile}
                onSave={handleJsonSave}
                onCancel={onClose}
                mode={mode}
              />
            </EditorContainer>
          )}
        </DialogBody>

        <DialogFooter>
          <Button onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button $primary onClick={handleSave} $disabled={isSaving}>
            {isSaving ? 'Saving...' : (mode === 'create' ? 'Create Profile' : 'Update Profile')}
          </Button>
        </DialogFooter>
      </DialogContainer>
    </DialogOverlay>
  );
}; 